import React, {
  useEffect,
  useRef,
  type JSXElementConstructor,
  useState,
  useMemo,
} from 'react';
import { PreviewerItem } from './previewerItem';
import { Hooks } from '#packages/apilib';
import type {
  DisplayConfig,
  PinElementPositionConfig,
  PreviewerEffectsMapping,
} from '../types';
import styles from './preview.scss';
import {
  getDefaultBaseWidth,
  getFixedHeaderStyle,
  getPinElementStyle,
} from '../previewerUtils';

interface PreviewerItemData {
  title: string;
  ThumbnailComponent: JSXElementConstructor<{ onReady: Function }>;
  effectsMapping: PreviewerEffectsMapping;
  previewIndicator?: React.ReactElement;
  pinElementConfig?: PinElementPositionConfig;
  fixedHeaderConfig?: { height: number };
}

interface PreviewProps {
  previewItems: PreviewerItemData[];
  pinElementsItems: PreviewerItemData[];
  fixedHeader?: PreviewerItemData;
  displayConfig: DisplayConfig;
  onReady?: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export const Preview = ({
  previewItems,
  pinElementsItems,
  fixedHeader,
  displayConfig,
  onReady,
  containerRef,
}: PreviewProps) => {
  const {
    displayWidth,
    baseWidth,
    displayMaxHeight,
    shouldOverlay,
    isInteractive,
    isScrollable,
    isClickable,
  } = displayConfig;
  const previewRef = useRef<HTMLDivElement>(null);
  const enablePreviewAnimationsPromiseRef = useRef<Hooks.PromiseHook>(null);
  const awaitingReady = useRef<number>(
    previewItems?.length + pinElementsItems?.length,
  );
  const [visiblePreviewHeight, setVisiblePreviewHeight] = useState(0);
  const [previewHeight, setPreviewHeight] = useState(0);

  const scale = displayWidth / (baseWidth || getDefaultBaseWidth());

  useEffect(() => {
    enablePreviewAnimationsPromiseRef.current = Hooks.createPromiseHook();
    awaitingReady.current = previewItems?.length + pinElementsItems?.length;
  }, [previewItems, pinElementsItems, displayConfig]);

  function onPreviewItemReady(): () => Promise<void> {
    return async () => {
      awaitingReady.current--;
      console.log(`${awaitingReady.current} previews left to be ready`);
      if (awaitingReady.current === 0) {
        enablePreviewAnimationsPromiseRef.current.resolve();
        onReady?.();
      }
    };
  }

  const pinElementsHeight = useMemo(() => {
    return pinElementsItems.reduce((acc, { pinElementConfig }) => {
      return acc + pinElementConfig.height;
    }, 0);
  }, [pinElementsItems]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        const visiblePart =
          containerRef?.current?.clientHeight || entry.intersectionRect.height;

        setVisiblePreviewHeight(visiblePart);

        const clientHeight = previewRef.current.clientHeight;
        setPreviewHeight(
          clientHeight > pinElementsHeight
            ? clientHeight - pinElementsHeight
            : clientHeight || 0,
        );
      } else {
        setVisiblePreviewHeight(0);
      }
    });

    let currentPreviewRef: HTMLDivElement;

    if (previewRef.current) {
      observer.observe(previewRef.current);
      currentPreviewRef = previewRef.current;
    }

    return () => {
      if (currentPreviewRef) {
        observer.unobserve(currentPreviewRef);
      }
    };
  }, [containerRef, pinElementsHeight]);

  return (
    <div className={styles.preview} ref={previewRef}>
      {fixedHeader && (
        <div
          className={styles.previewFixedHeader}
          style={getFixedHeaderStyle(
            { height: fixedHeader.fixedHeaderConfig?.height },
            {
              scale,
            },
          )}
        >
          <PreviewerItem
            ThumbnailComponent={fixedHeader.ThumbnailComponent}
            displayWidth={displayWidth}
            baseWidth={baseWidth}
            displayMaxHeight={displayMaxHeight}
            shouldOverlay={shouldOverlay}
            isInteractive={isInteractive}
            isScrollable={isScrollable}
            isClickable={isClickable}
            enablePreviewAnimationsPromise={
              enablePreviewAnimationsPromiseRef.current?.promise
            }
            effectsMapping={fixedHeader.effectsMapping}
            onReady={onPreviewItemReady()}
          />
          {fixedHeader.previewIndicator}
        </div>
      )}

      {previewHeight > 0 &&
        visiblePreviewHeight > 0 &&
        pinElementsItems.map(
          (
            {
              ThumbnailComponent,
              effectsMapping,
              previewIndicator,
              pinElementConfig,
            },
            index,
          ) => {
            const pinElementStyle = getPinElementStyle(pinElementConfig, {
              scale,
              visiblePreviewHeight,
              previewHeight,
              displayWidth,
            });

            return (
              <div
                key={index}
                className={styles.previewPinElement}
                style={pinElementStyle}
              >
                <PreviewerItem
                  ThumbnailComponent={ThumbnailComponent}
                  displayWidth={displayWidth}
                  baseWidth={baseWidth}
                  displayMaxHeight={pinElementStyle.height}
                  shouldOverlay
                  isInteractive={isInteractive}
                  isScrollable={isScrollable}
                  isClickable={isClickable}
                  enablePreviewAnimationsPromise={
                    enablePreviewAnimationsPromiseRef.current?.promise
                  }
                  effectsMapping={effectsMapping}
                  onReady={onPreviewItemReady()}
                />
                {previewIndicator}
              </div>
            );
          },
        )}

      {previewItems.map(
        ({ ThumbnailComponent, effectsMapping, previewIndicator }, index) => (
          <div key={index} className={styles.previewItemWrapper}>
            <PreviewerItem
              ThumbnailComponent={ThumbnailComponent}
              displayWidth={displayWidth}
              baseWidth={baseWidth}
              displayMaxHeight={displayMaxHeight}
              shouldOverlay={shouldOverlay}
              isInteractive={isInteractive}
              isScrollable={isScrollable}
              isClickable={isClickable}
              enablePreviewAnimationsPromise={
                enablePreviewAnimationsPromiseRef.current?.promise
              }
              effectsMapping={effectsMapping}
              onReady={onPreviewItemReady()}
            />
            {previewIndicator}
          </div>
        ),
      )}
    </div>
  );
};
